import {useState, useCallback, useEffect, useMemo} from 'react';
import produce from 'immer';
import {useSMCSnackbar} from '../../../utils/hooks';
import {useTranslation} from 'react-i18next';
import {setProperty, isAxiosAuthError, isStringI18n, extractI18nString} from '../../../utils/utils';
import Api from '../../../api/SMPFetchHelper';
import {ObjectTypes, ErrorCodes} from '../../../api/consts';
import store from '../../../store';
import {push} from 'connected-react-router';
import {SMCPaths} from '../../../conf/Routes';
import {useSMCErrorHandler} from '../../../utils/errorUtils';
import {PlanGetSchema} from '../../../api/DBSchema/Plan';
import {SessionDataGetSchema} from '../../../api/DBSchema/SessionData';
import PlanGeneral from './InnerPages/PlanGeneral';
import PlanGatewayActivation from './InnerPages/PlanGatewayActivation';
import PlanNtp from './InnerPages/PlanNtp';
import PlanTimezone from './InnerPages/PlanTimezone';
import PlanDns from './InnerPages/PlanDns';
import PlanAdministrators from './InnerPages/PlanAdministrators';
import PlanFirewall from './InnerPages/PlanFirewall';
import PlanAccessPolicy from './InnerPages/PlanAccessPolicy';
import PlanAntiSpam from './InnerPages/PlanAntiSpam';
import PlanRemoteAccess from './InnerPages/PlanRemoteAccess';
import PlanQos from './InnerPages/PlanQos';
import PlanThreatEmulation from './InnerPages/PlanThreatEmulation';
import PlanUserAwareness from './InnerPages/PlanUserAwareness';
import PlanUnifiedThreatPrevention from './InnerPages/PlanUnifiedThreatPrevention';
import PlanThreatPreventionExceptions from './InnerPages/PlanThreatPreventionExceptions';
import PlanThreatPrevention from './InnerPages/PlanThreatPrevention';
import PlanAntiVirus from './InnerPages/PlanAntiVirus';
import PlanSiteToSiteVpn from './InnerPages/PlanSiteToSiteVpn';
import PlanPeriodicBackup from './InnerPages/PlanPeriodicBackup';
import PlanAntiBot from './InnerPages/PlanAntiBot';
import PlanSSLInspection from './InnerPages/PlanSSLInspection';
import PlanManagedServices from './InnerPages/PlanManagedServices';
import PlanReports from './InnerPages/PlanReports';
import PlanReportRecipients from './InnerPages/PlanReportRecipients';
import PlanNotifications from './InnerPages/PlanNotifications';
import PlanNotificationsRecipients from './InnerPages/PlanNotificationsRecipients';
import PlanGatewaysBehindNat from './InnerPages/PlanGatewaysBehindNat';
import PlanAdministratorAccess from './InnerPages/PlanAdministratorAccess';
import PlanCliScript1 from './InnerPages/PlanCliScript1';
import PlanCliScript2 from './InnerPages/PlanCliScript2';
import PlanCliScript3 from './InnerPages/PlanCliScript3';
import PlanCliScript4 from './InnerPages/PlanCliScript4';
import PlanCliScript5 from './InnerPages/PlanCliScript5';
import PlanApplicationsAndUrls from './InnerPages/PlanApplicationsAndUrls';
import PlanIps from './InnerPages/PlanIps';
import generalIcon from '../../../assets/images/general.png';
import gatewayActivationIcon from '../../../assets/images/gateway_activation.png';
import ownerIcon from '../../../assets/images/owner.png';
import administratorIcon from '../../../assets/images/administrator.svg';
import exceptionsIcon from '../../../assets/images/exceptions.png';
import deviceSettingsIcon from '../../../assets/images/device-settings.png';
import ntpIcon from '../../../assets/images/ntp.png';
import timezoneIcon from '../../../assets/images/timezone.png';
import dnsIcon from '../../../assets/images/dns.png';
import securityIcon from '../../../assets/images/security.png';
import firewallIcon from '../../../assets/images/firewall_16.png';
import fwRulesIcon from '../../../assets/images/fw_rules.png';
import appControlIcon from '../../../assets/images/app_control_16.png';
import ipsIcon from '../../../assets/images/ips_16.png';
import antiVirusIcon from '../../../assets/images/anti_virus_16.png';
import antiSpamIcon from '../../../assets/images/anti_spam_16.png';
import qosIcon from '../../../assets/images/qos_16.png';
import vpnRemoteIcon from '../../../assets/images/vpn_remote_16.png';
import vpnSiteIcon from '../../../assets/images/vpn_site_16.png';
import idaIcon from '../../../assets/images/ida_16.png';
import antiBotIcon from '../../../assets/images/anti_bot_16.png';
import threatEmulationIcon from '../../../assets/images/threat_emulation_16.png';
import policyIcon from '../../../assets/images/policy.png';
import sslInspectionIcon from '../../../assets/images/ssl_inspection_16.png';
import servicesIcon from '../../../assets/images/services.png';
import settingsIcon from '../../../assets/images/settings.png';
import vpnIcon from '../../../assets/images/vpn_site_16.png';
import communityIcon from '../../../assets/images/community.png';
import updatesIcon from '../../../assets/images/updates.png';
import backupIcon from '../../../assets/images/backup.png';
import reportsIcon from '../../../assets/images/reports.png';
import administratorAccessIcon from '../../../assets/images/administrator_access_16.png';
import gatewaysBehindNatIcon from '../../../assets/images/gateways_behind_nat_16.png';
import scriptIcon from '../../../assets/images/script.png';
import historyIcon from '../../../assets/images/history.png';
import PlanTraditionalAntiVirus from './InnerPages/PlanTraditionalAntiVirus';
import PlanFirmware from './InnerPages/PlanFirmware';
import PlanHistory from './InnerPages/PlanHistory';
import PlanVpnCommunity from './InnerPages/PlanVpnCommunity';
import PlanCliScriptFromApi from './InnerPages/PlanCliScriptFromApi';
import {helpContextPageUrl} from '../../../conf/Routes';
import PlanLocalUsers from "./InnerPages/PlanLocalUsers";

export function usePlanEditor(planId, defaultChosenPageId) {
    const { t } = useTranslation();
    const { enqueueSuccessSnackbar, enqueueErrorSnackbar, enqueueLogoutSnackbar } = useSMCSnackbar();
    const { getErrorMessage, getMessagesArrayByPages } = useSMCErrorHandler();

    const [origPlanObject, setOrigPlanObject] = useState({});
    const [planObject, setPlanObject] = useState({});
    const [availableFirmwares, setAvailableFirmwares] = useState({});
    const [isSMEReportEnabled, setIsSMEReportEnabled] = useState(false);
    const [isReportConfirmationEnabled, setIsReportConfirmationEnabled] = useState(false);
    const [isHasEditPermissionForReportsConfig, setIsHasEditPermissionForReportsConfig] = useState(false);

    const [isPageDataLoaded, setIsPageDataLoaded] = useState(false);
    const [isInnerPageLoading, setIsInnerPageLoading] = useState(false);
    const [isPageLoadFailed, setIsPageLoadFailed] = useState(false);
    const [errorMessagesByPageList, setErrorMessagesByPageList] = useState(null);

    const [isObjectDirty, setIsObjectDirty] = useState(false);
    
    const [chosenPageId, setChosenPageId] = useState(defaultChosenPageId || 'general');

    const [appiData, setAppiData] = useState([]);
    const [uoTableData, setUoData] = useState([]);
    const [zonesData, setZonesData] = useState([]);

    const editorPages = useMemo(() => (
        [
            { id: PlanGetSchema.pages.general.pageId, title: t('SMP.GENERAL'), icon: generalIcon, pageElement: PlanGeneral, helpId: helpContextPageUrl.planGeneral },
            { id: PlanGetSchema.pages.gatewayActivation.pageId, title: t('SMP.PLANS.INNER_MENU.GATEWAY_ACTIVATION'), icon: gatewayActivationIcon, pageElement: PlanGatewayActivation, helpId: helpContextPageUrl.planGatewayActivation },
            { title: t('SMP.PLANS.INNER_MENU.DEVICE_SETTINGS'), icon: deviceSettingsIcon, children: [
                    { id: PlanGetSchema.pages.ntp.pageId, title: t('SMP.PLANS.INNER_MENU.NTP'), icon: ntpIcon, pageElement: PlanNtp, helpId: helpContextPageUrl.planNtp },
                    { id: PlanGetSchema.pages.timezone.pageId, title: t('SMP.PLANS.INNER_MENU.TIME_ZONE'), icon: timezoneIcon, pageElement: PlanTimezone, helpId: helpContextPageUrl.planTimezone },
                    { id: PlanGetSchema.pages.dns.pageId, title: t('SMP.PLANS.INNER_MENU.DNS'), icon: dnsIcon, pageElement: PlanDns, helpId: helpContextPageUrl.planDns },
                    { id: PlanGetSchema.pages.administrators.pageId, title: t('SMP.PLANS.INNER_MENU.ADMINISTRATORS'), icon: administratorIcon, pageElement: PlanAdministrators, helpId: helpContextPageUrl.planAdministrators },
                    { id: PlanGetSchema.pages.localUsers.pageId, title: t('SMP.PLANS.INNER_MENU.LOCAL_USERS'), icon: ownerIcon, pageElement: PlanLocalUsers, helpId: helpContextPageUrl.gatewayLocalUsers },
                ]
            },
            { title: t('SMP.PLANS.INNER_MENU.SECURITY_SOFTWARE_BLADES'), icon: securityIcon, children: [
                    { id: PlanGetSchema.pages.firewall.pageId, title: t('SMP.PLANS.INNER_MENU.FIREWALL'), icon: firewallIcon, pageElement: PlanFirewall, helpId: helpContextPageUrl.planFirewall },
                    { id: PlanGetSchema.pages.accessPolicy.pageId, title: t('SMP.PLANS.INNER_MENU.ACCESS_POLICY'), icon: fwRulesIcon, pageElement: PlanAccessPolicy, helpId: helpContextPageUrl.planAccessPolicy },
                    { id: PlanGetSchema.pages.applicationsAndUrls.pageId, title: t('SMP.PLANS.INNER_MENU.APPLICATIONS_AND_URLS'), icon: appControlIcon, pageElement: PlanApplicationsAndUrls, helpId: helpContextPageUrl.planApplicationsAndUrls },
                    { id: PlanGetSchema.pages.ips.pageId, title: t('SMP.PLANS.INNER_MENU.IPS'), icon: ipsIcon, pageElement: PlanIps, helpId: helpContextPageUrl.planIps },
                    { id: PlanGetSchema.pages.traditionalAntiVirus.pageId, title: t('SMP.PLANS.INNER_MENU.T_ANTI_VIRUS'), icon: antiVirusIcon, pageElement: PlanTraditionalAntiVirus, helpId: helpContextPageUrl.planTraditionalAntiVirus },
                    { id: PlanGetSchema.pages.antiSpam.pageId, title: t('SMP.PLANS.INNER_MENU.ANTI_SPAM'), icon: antiSpamIcon, pageElement: PlanAntiSpam, helpId: helpContextPageUrl.planAntiSpam },
                    { id: PlanGetSchema.pages.qos.pageId, title: t('SMP.PLANS.INNER_MENU.QOS'), icon: qosIcon, pageElement: PlanQos, helpId: helpContextPageUrl.planQos },
                    { id: PlanGetSchema.pages.remoteAccess.pageId, title: t('SMP.PLANS.INNER_MENU.REMOTE_ACCESS'), icon: vpnRemoteIcon, pageElement: PlanRemoteAccess, helpId: helpContextPageUrl.planRemoteAccess },
                    { id: PlanGetSchema.pages.siteToSiteVpn.pageId, title: t('SMP.PLANS.INNER_MENU.SITE_TO_SITE_VPN'), icon: vpnSiteIcon, pageElement: PlanSiteToSiteVpn, helpId: helpContextPageUrl.planSiteToSiteVpn },
                    { id: PlanGetSchema.pages.userAwareness.pageId, title: t('SMP.PLANS.INNER_MENU.USER_AWARNESS'), icon: idaIcon, pageElement: PlanUserAwareness, helpId: helpContextPageUrl.planserAwareness },
                    { id: PlanGetSchema.pages.antiVirus.pageId, title: t('SMP.PLANS.INNER_MENU.ANTI_VIRUS'), icon: antiVirusIcon, pageElement: PlanAntiVirus, helpId: helpContextPageUrl.planAntiVirus },
                    { id: PlanGetSchema.pages.antiBot.pageId, title: t('SMP.PLANS.INNER_MENU.ANTI_BOT'), icon: antiBotIcon, pageElement: PlanAntiBot, helpId: helpContextPageUrl.planAntiBot },
                    { id: PlanGetSchema.pages.threatEmulation.pageId, title: t('SMP.PLANS.INNER_MENU.THREAT_EMULATION'), icon: threatEmulationIcon, pageElement: PlanThreatEmulation, helpId: helpContextPageUrl.planThreatEmulation },
                    { id: PlanGetSchema.pages.threatPrevention.pageId, title: t('SMP.PLANS.INNER_MENU.THREAT_PREVENTION'), icon: policyIcon, pageElement: PlanThreatPrevention, helpId: helpContextPageUrl.planThreatPrevention },
                    { id: PlanGetSchema.pages.unifiedThreatPrevention.pageId, title: t('SMP.PLANS.INNER_MENU.UNIFIED_THREAT_PREVENTION'), icon: policyIcon, pageElement: PlanUnifiedThreatPrevention, helpId: helpContextPageUrl.planThreatPrevention },
                    { id: PlanGetSchema.pages.threatPreventionExceptions.pageId, title: t('SMP.PLANS.INNER_MENU.THREAT_PREVENTION_EXCEPTIONS'), icon: exceptionsIcon, pageElement: PlanThreatPreventionExceptions, helpId: helpContextPageUrl.planThreatPreventionExceptions },
                    { id: PlanGetSchema.pages.sslInspection.pageId, title: t('SMP.PLANS.INNER_MENU.SSL_INSPECTION'), icon: sslInspectionIcon, pageElement: PlanSSLInspection, helpId: helpContextPageUrl.planSslInspection },
                ]
            },
            { title: t('SMP.PLANS.INNER_MENU.SERVICES'), icon: servicesIcon, children: [
                    { id: PlanGetSchema.pages.managedServices.pageId, title: t('SMP.PLANS.INNER_MENU.MANAGED_SERVICES'), icon: settingsIcon, pageElement: PlanManagedServices, helpId: helpContextPageUrl.planManagedServices },
                    { id: PlanGetSchema.pages.firmware.pageId, title: t('SMP.PLANS.INNER_MENU.FIRMWARE'), icon: updatesIcon, pageElement: PlanFirmware, helpId: helpContextPageUrl.planFirmware },
                    { id: PlanGetSchema.pages.serviceBackup.pageId, title: t('SMP.PLANS.INNER_MENU.PERIODIC_BACKUP'), icon: backupIcon, pageElement: PlanPeriodicBackup, helpId: helpContextPageUrl.planServiceBackup },
                    { id: PlanGetSchema.pages.reports.pageId, title: t('SMP.PLANS.INNER_MENU.REPORTS'), icon: reportsIcon, pageElement: PlanReports, helpId: helpContextPageUrl.planReports },
                    { id: PlanGetSchema.pages.reportRecipients.pageId, title: t('SMP.PLANS.INNER_MENU.REPORT_RECIPIENTS'), icon: reportsIcon, pageElement: PlanReportRecipients, helpId: helpContextPageUrl.planReportRecipients },
                    { id: PlanGetSchema.pages.notifications.pageId, title: t('SMP.PLANS.INNER_MENU.NOTIFICATIONS'), icon: backupIcon, pageElement: PlanNotifications, helpId: helpContextPageUrl.planNotifications },
                    { id: PlanGetSchema.pages.notificationsRecipients.pageId, title: t('SMP.PLANS.INNER_MENU.NOTIFICATIONS_RECIPIENTS'), icon: reportsIcon, pageElement: PlanNotificationsRecipients, helpId: helpContextPageUrl.planNotificationRecipients }
                ]
            },
            { title: t('SMP.PLANS.INNER_MENU.VPN'), icon: vpnIcon, children: [
                    { id: PlanGetSchema.pages.vpnCommunity.pageId, title: t('SMP.PLANS.INNER_MENU.COMMUNITY'), icon: communityIcon, pageElement: PlanVpnCommunity, helpId: helpContextPageUrl.planVpnCommunity },
                ]
            },
            { title: t('SMP.PLANS.INNER_MENU.SETUP'), icon: settingsIcon, children: [
                    { id: PlanGetSchema.pages.administratorAccess.pageId, title: t('SMP.PLANS.INNER_MENU.ADMINISTRATORS_ACCESS'), icon: administratorAccessIcon, pageElement: PlanAdministratorAccess, helpId: helpContextPageUrl.administratorAccess },
                    { id: PlanGetSchema.pages.gatewaysBehindNAT.pageId, title: t('SMP.PLANS.INNER_MENU.GATEWAYS_BEHIND_NAT'), icon: gatewaysBehindNatIcon, pageElement: PlanGatewaysBehindNat, helpId: helpContextPageUrl.gatewaysBehindNAT },
                ]
            },
            { title: t('SMP.PLANS.INNER_MENU.CLI_SCRIPTS'), icon: scriptIcon, children: [
                    { id: PlanGetSchema.pages.cliScript1.pageId, title: t('SMP.PLANS.INNER_MENU.CLI_SCRIPT_1'), icon: scriptIcon, pageElement: PlanCliScript1, helpId: helpContextPageUrl.cliScript },
                    { id: PlanGetSchema.pages.cliScript2.pageId, title: t('SMP.PLANS.INNER_MENU.CLI_SCRIPT_2'), icon: scriptIcon, pageElement: PlanCliScript2, helpId: helpContextPageUrl.cliScript },
                    { id: PlanGetSchema.pages.cliScript3.pageId, title: t('SMP.PLANS.INNER_MENU.CLI_SCRIPT_3'), icon: scriptIcon, pageElement: PlanCliScript3, helpId: helpContextPageUrl.cliScript },
                    { id: PlanGetSchema.pages.cliScript4.pageId, title: t('SMP.PLANS.INNER_MENU.CLI_SCRIPT_4'), icon: scriptIcon, pageElement: PlanCliScript4, helpId: helpContextPageUrl.cliScript },
                    { id: PlanGetSchema.pages.cliScript5.pageId, title: t('SMP.PLANS.INNER_MENU.CLI_SCRIPT_5'), icon: scriptIcon, pageElement: PlanCliScript5, helpId: helpContextPageUrl.cliScript },
                    { id: PlanGetSchema.pages.cliScriptFromApi.pageId, title: t('SMP.PLANS.INNER_MENU.CLI_SCRIPT_FROM_API'), icon: scriptIcon, pageElement: PlanCliScriptFromApi, helpId: helpContextPageUrl.cliScript },
                ]
            },
            { id: PlanGetSchema.pages.history.pageId, title: t('SMP.HISTORY'), icon: historyIcon, pageElement: PlanHistory, helpId: helpContextPageUrl.planHistory }
        ]
    ), [t]);

    const [pageIdToPageName, pageIdToHelpId] = useMemo(() => {
        const _pageIdToPageName = {};
        const _pageIdToHelpId = {};
        const addPagesData = (pagesRoot) => {
            pagesRoot.forEach(page => {
                if (page.id) {
                    _pageIdToPageName[page.id] = page.title;
                    _pageIdToHelpId[page.id] = page.helpId;
                }
                if (page.children) {
                    addPagesData(page.children);
                }
            })
        }
        addPagesData(editorPages);
        return [_pageIdToPageName, _pageIdToHelpId];
    }, [editorPages]);

    const helpId = useMemo(() => pageIdToHelpId[chosenPageId], [pageIdToHelpId, chosenPageId]);

    const setAppiDataValue = function(appiDataValue){
        if(appiData.length === 0 && appiDataValue!==null){
            setAppiData(appiDataValue);
        }
    }

    const setUoTableDataValue = function(uoTableDataValue){
        if( uoTableData.length === 0 && uoTableDataValue!==null){
            setUoData(uoTableDataValue);
        }
    }

    const fetchPlan = useCallback(async () => {
        setIsPageDataLoaded(false);
        try {
            const fetchesToExecute = [];
            fetchesToExecute.push(new Promise((resolve, reject) => {
                Api.fetchSingleObject(ObjectTypes.PLAN, planId)
                .then(resp => {
                    const plan = resp.data.data.plan;
                    if (!plan) { throw new Error(ErrorCodes.SERVER_ERROR); }
                    setOrigPlanObject(plan);
                    setPlanObject(plan);
                    setIsObjectDirty(false); // Just fetched, not dirty
                    resolve();
                })
                .catch(reject);
            }));
            fetchesToExecute.push(new Promise((resolve, reject) => {
                Api.getSessionData({
                    attributes: [
                        SessionDataGetSchema.isSMEReportEnabled,
                        SessionDataGetSchema.isReportConfirmationEnabled,
                        SessionDataGetSchema.isHasEditPermissionForReportsConfig
                    ]
                })
                .then(resp => {
                    setIsSMEReportEnabled(resp.data[SessionDataGetSchema.isSMEReportEnabled]);
                    setIsReportConfirmationEnabled(resp.data[SessionDataGetSchema.isReportConfirmationEnabled]);
                    setIsHasEditPermissionForReportsConfig(resp.data[SessionDataGetSchema.isHasEditPermissionForReportsConfig]);
                    resolve();
                })
                .catch(reject);
            }));
            // Fetch firmwares available on this domain
            fetchesToExecute.push(new Promise((resolve) => {
                Api.getFirmwareListByVersion()
                .then(resp => {
                    setAvailableFirmwares(resp.data.data);
                    resolve();
                })
                .catch(() => {
                    console.error("Error fetching firmwares");
                    resolve();
                });
            }));

            // Get Zones list
            fetchesToExecute.push(new Promise((resolve) => {
                Api.getZoneList()
                .then((resp) => {
                    const zonesData = resp.data.data;
                    setZonesData(zonesData);
                    resolve();
                }) 
                .catch(() => {
                    console.error("Error fetching zones list")
                    resolve();
                });
            }));
            // Wait for all fetches to complete
            await Promise.all(fetchesToExecute);
            setIsPageLoadFailed(false);
        } catch (e) {
            const errorMessage = getErrorMessage(e);
            if (isAxiosAuthError(e)) {
                enqueueLogoutSnackbar(errorMessage);
            } else {
                enqueueErrorSnackbar(errorMessage);
            }
            setIsPageLoadFailed(true);
        }
        setIsPageDataLoaded(true);
    }, [planId, getErrorMessage, enqueueErrorSnackbar, enqueueLogoutSnackbar]);

    const setPlanData = useCallback((paramPath, newValue) => {
        setIsObjectDirty(true); // Data changed, object is dirty
        setPlanObject(planObj => produce(planObj, draft => {
            setProperty(draft, paramPath, newValue);
        }));
    }, []);

    const verifyData = useCallback(() => {
        setIsInnerPageLoading(true);
        Api.verifySingleObject(ObjectTypes.PLAN, planId, planObject)
        .then(() => {
            // On no error, clear error message
            setErrorMessagesByPageList(null);
        })
        .catch((error) => {
            const errorMessageArrayByPage = getMessagesArrayByPages(error, pageIdToPageName);
            if (errorMessageArrayByPage) {
                setErrorMessagesByPageList(errorMessageArrayByPage);
            }
        })
        .finally(() => {
            setIsInnerPageLoading(false);
        });
    }, [planId, getMessagesArrayByPages, planObject, pageIdToPageName]);

    const onPageChange = useCallback((pageId) => {
        verifyData();
        setChosenPageId(pageId);
    }, [verifyData]);

    const saveButtonOnClick = useCallback(() => {
        Api.updateSingleObject(ObjectTypes.PLAN, planId, planObject)
        .then(() => {
            setIsPageDataLoaded(false);
            setErrorMessagesByPageList(null);
            fetchPlan();
            enqueueSuccessSnackbar(t('OBJECT_EDITOR_OBJ_SAVED_OK'));
        })
        .catch((error) => {
                const errorMessage = getErrorMessage(error);
                alert(errorMessage);
        })
        .finally(() => {
            setIsPageDataLoaded(true);
        });
    }, [planId, getErrorMessage, fetchPlan, planObject, enqueueSuccessSnackbar, t]);

    const deleteButtonOnClick = useCallback(() => {
        if (window.confirm(t('OBJECT_EDITOR_OBJ_DELETE', [planId] ))) {
            Api.deleteObjects(ObjectTypes.PLAN, [planId])
            .then(res => {
                const deleteResult = res.data.results[0];
                if (deleteResult.success) {
                    setIsObjectDirty(false); // Set as false to not trigger dirty modal
                    store.dispatch(push(SMCPaths.plans));
                    enqueueSuccessSnackbar(t('OBJECT_EDITOR_OBJ_DELETE_OK', [planId]));
                } else {
                    const deleteErrorCode = deleteResult.errorCode;
                    let deleteErrorParams;
                    if (deleteResult.errorParams) {
                        deleteErrorParams = deleteResult.errorParams.map(param => (
                            isStringI18n(param)) ? t(extractI18nString(param)) : param
                        );
                    }
                    enqueueErrorSnackbar(t(extractI18nString(deleteErrorCode), deleteErrorParams));
                }
            })
            .catch(err => {
                const errorCode = getErrorMessage(err);
                enqueueErrorSnackbar(t(errorCode));
            });
        }
    }, [planId, getErrorMessage, enqueueSuccessSnackbar, enqueueErrorSnackbar, t]);

    const revertButtonOnClick = useCallback(() => {
        setPlanObject(origPlanObject);
        setIsObjectDirty(false); // Is not dirty, object is reset to originl values
        setErrorMessagesByPageList(null);
    }, [origPlanObject]);
    
    // Fetch plan on loading of page
    useEffect(() => {
        fetchPlan();
    }, [fetchPlan]);
    
    return {
        planObject,
        origPlanObject,
        isObjectDirty,
        editorPages,
        helpId,
        availableFirmwares,
        isSMEReportEnabled,
        isReportConfirmationEnabled,
        isHasEditPermissionForReportsConfig,
        isPageDataLoaded,
        isInnerPageLoading,
        isPageLoadFailed,
        chosenPageId,
        errorMessagesByPageList,
        onPageChange,
        setPlanData,
        saveButtonOnClick,
        revertButtonOnClick,
        reloadPageData: fetchPlan,
        deleteButtonOnClick,
        appiData,
        uoTableData,
        zonesData,
        setAppiDataValue,
        setUoTableDataValue,
    };
}